import * as React from 'react';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Skeleton,
    TextField,
    Grid,
    Typography
} from '@mui/material';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import CustomerTrendChart from './CustomerTrendChart';
import CustomerConversionChart from './CustomerConversionChart';
import VisitorTrendChart from './VisitorTrendChart';
import VisitorBreakdownCharts from './VisitorBreakdownCharts';
import VisitorLoginSplitChart from './VisitorLoginSplitChart';
import VisitorOnboardingSplitChart from './VisitorOnboardingSplitChart';
import NewCustomerOnboardingChart from './NewCustomerOnboardingChart';
import CustomerBaseProfilePieChart from './CustomerBaseProfilePieChart';
import {
    ActiveDormantCustomersChart,
    ActiveDormantBucketSlug,
    NewReturningVisitorConversionChart,
    TimeToRegisterProfileChart,
} from './CustomerInsightCharts';
import ActiveDormantCustomerModal from './ActiveDormantCustomerModal';
import DashboardPanelHeader from '../components/DashboardPanelHeader';
import axios from 'axios';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';

const DashboardCustomers = () => {
    useAuthorize(DASHBOARD_MODULE_ROLES);

    const normalizeArrayPayload = (payload: any): any[] => {
        let parsed = payload;
        if (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch (_error) {
                return [];
            }
        }

        if (Array.isArray(parsed)) {
            return parsed;
        }
        if (Array.isArray(parsed?.data)) {
            return parsed.data;
        }
        if (Array.isArray(parsed?.rows)) {
            return parsed.rows;
        }
        if (Array.isArray(parsed?.results)) {
            return parsed.results;
        }
        if (parsed && typeof parsed === 'object') {
            const values = Object.values(parsed);
            if (values.length && values.every((item: any) => item && typeof item === 'object')) {
                return values as any[];
            }
        }
        return [];
    };

    // Default date range: first day of the current year through today.
    const getDefaultDateRange = () => {
        const now = new Date();
        const startDate = startOfYear(now);

        return {
            fromDate: format(startDate, 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd')
        };
    };

    const defaultDates = getDefaultDateRange();

    const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
    const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
    const [toDate, setToDate] = useState<string>(defaultDates.toDate);
    const [quickRange, setQuickRange] = useState<string>('');
    const [trendData, setTrendData] = useState<any[]>([]);
    const [conversionData, setConversionData] = useState<any[]>([]);
    const [visitorData, setVisitorData] = useState<any[]>([]);
    const [visitorLoginSplit, setVisitorLoginSplit] = useState<any[]>([]);
    const [visitorOnboardingSplit, setVisitorOnboardingSplit] = useState<any[]>([]);
    const [newCustomerOnboardingSplit, setNewCustomerOnboardingSplit] = useState<any[]>([]);
    const [newProfilesCreatedTrends, setNewProfilesCreatedTrends] = useState<any[]>([]);
    const [customerBaseProfileSplit, setCustomerBaseProfileSplit] = useState<any | null>(null);
    const [customerInsights, setCustomerInsights] = useState<any | null>(null);
    const [visitorBreakdowns, setVisitorBreakdowns] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [conversionLoading, setConversionLoading] = useState(true);
    const [visitorLoading, setVisitorLoading] = useState(true);
    const [visitorLoginSplitLoading, setVisitorLoginSplitLoading] = useState(true);
    const [visitorOnboardingSplitLoading, setVisitorOnboardingSplitLoading] = useState(true);
    const [newCustomerOnboardingSplitLoading, setNewCustomerOnboardingSplitLoading] = useState(true);
    const [newProfilesCreatedTrendsLoading, setNewProfilesCreatedTrendsLoading] = useState(true);
    const [customerBaseProfileSplitLoading, setCustomerBaseProfileSplitLoading] = useState(true);
    const [customerInsightsLoading, setCustomerInsightsLoading] = useState(true);
    const [visitorBreakdownLoading, setVisitorBreakdownLoading] = useState(true);
    const [dataThrough, setDataThrough] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
    const [activeDormantModalOpen, setActiveDormantModalOpen] = useState(false);
    const [selectedActiveDormantBucket, setSelectedActiveDormantBucket] = useState<ActiveDormantBucketSlug | null>(null);
    const [selectedActiveDormantLabel, setSelectedActiveDormantLabel] = useState('');
    const [selectedActiveDormantCount, setSelectedActiveDormantCount] = useState(0);

    const handleActiveDormantBarClick = (bucket: ActiveDormantBucketSlug, label: string, count: number) => {
        if (count <= 0) {
            return;
        }
        setSelectedActiveDormantBucket(bucket);
        setSelectedActiveDormantLabel(label);
        setSelectedActiveDormantCount(count);
        setActiveDormantModalOpen(true);
    };

    const handleActiveDormantModalClose = () => {
        setActiveDormantModalOpen(false);
    };

    const captureDataThrough = (response: any) => {
        const nextDataThrough =
            response.headers?.['x-analytics-data-through'] ||
            response.data?.data_through ||
            (Array.isArray(response.data) ? response.data[0]?.data_through : null);

        const nextLastUpdated =
            response.headers?.['x-analytics-last-updated'] ||
            response.data?.last_updated_at ||
            (Array.isArray(response.data) ? response.data[0]?.last_updated_at : null);

        if (nextDataThrough) {
            setDataThrough(nextDataThrough);
        }
        if (nextLastUpdated) {
            setLastUpdatedAt(nextLastUpdated);
        }
    };

    useEffect(() => {
        const fetchTrendData = async () => {
            setLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching customer trends with params:', {
                apiUrl,
                params,
                fullUrl: `${apiUrl}?period=${period}&from_date=${fromDate}&to_date=${toDate}`
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ API Response Status:', response.status);
                console.log('✅ Customer trends data:', response.data);
                const normalized = normalizeArrayPayload(response.data);
                console.log('✅ Data length:', normalized.length);
                captureDataThrough(response);
                if (!normalized.length) {
                    console.warn('⚠️ Customer trends payload is empty or non-array:', response.data);
                }
                setTrendData(normalized);
            } catch (error: any) {
                console.error('❌ Error fetching customer trends:', error);
                console.error('❌ Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    statusText: error.response?.statusText
                });
                setTrendData([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchConversionData = async () => {
            setConversionLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/conversion-trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching conversion trends with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Conversion API Response Status:', response.status);
                console.log('✅ Conversion trends data:', response.data);
                captureDataThrough(response);

                if (response.data && Array.isArray(response.data)) {
                    setConversionData(response.data);
                } else {
                    console.warn('⚠️ Conversion response data is not an array:', response.data);
                    setConversionData([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching conversion trends:', error);
                setConversionData([]);
            } finally {
                setConversionLoading(false);
            }
        };

        const fetchVisitorData = async () => {
            setVisitorLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/visitor-trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching visitor trends with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Visitor Trends API Response Status:', response.status);
                console.log('✅ Visitor trends data:', response.data);
                captureDataThrough(response);

                if (response.data && Array.isArray(response.data)) {
                    setVisitorData(response.data);
                } else {
                    console.warn('⚠️ Visitor response data is not an array:', response.data);
                    setVisitorData([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching visitor trends:', error);
                setVisitorData([]);
            } finally {
                setVisitorLoading(false);
            }
        };

        const fetchVisitorBreakdowns = async () => {
            setVisitorBreakdownLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/visitor-breakdowns`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching visitor breakdowns with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Visitor breakdowns API Response Status:', response.status);
                captureDataThrough(response);
                setVisitorBreakdowns(response.data || null);
            } catch (error: any) {
                console.error('❌ Error fetching visitor breakdowns:', error);
                setVisitorBreakdowns(null);
            } finally {
                setVisitorBreakdownLoading(false);
            }
        };

        const fetchVisitorLoginSplit = async () => {
            setVisitorLoginSplitLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/visitor-login-split`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching visitor login split with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Visitor Login Split API Response Status:', response.status);
                captureDataThrough(response);
                if (response.data && Array.isArray(response.data)) {
                    setVisitorLoginSplit(response.data);
                } else {
                    setVisitorLoginSplit([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching visitor login split:', error);
                setVisitorLoginSplit([]);
            } finally {
                setVisitorLoginSplitLoading(false);
            }
        };

        const fetchCustomerInsights = async () => {
            setCustomerInsightsLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/customer-insights`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching customer insights with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Customer Insights API Response Status:', response.status);
                captureDataThrough(response);
                setCustomerInsights(response.data || null);
            } catch (error: any) {
                console.error('❌ Error fetching customer insights:', error);
                setCustomerInsights(null);
            } finally {
                setCustomerInsightsLoading(false);
            }
        };

        const fetchVisitorOnboardingSplit = async () => {
            setVisitorOnboardingSplitLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/visitor-onboarding-split`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            try {
                const response = await axios.get(apiUrl, { params });
                captureDataThrough(response);
                if (response.data && Array.isArray(response.data)) {
                    setVisitorOnboardingSplit(response.data);
                } else {
                    setVisitorOnboardingSplit([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching visitor onboarding split:', error);
                setVisitorOnboardingSplit([]);
            } finally {
                setVisitorOnboardingSplitLoading(false);
            }
        };

        const fetchNewCustomerOnboardingSplit = async () => {
            setNewCustomerOnboardingSplitLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/new-customer-onboarding-split`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            try {
                const response = await axios.get(apiUrl, { params });
                captureDataThrough(response);
                if (response.data && Array.isArray(response.data)) {
                    setNewCustomerOnboardingSplit(response.data);
                } else {
                    setNewCustomerOnboardingSplit([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching new customer onboarding split:', error);
                setNewCustomerOnboardingSplit([]);
            } finally {
                setNewCustomerOnboardingSplitLoading(false);
            }
        };

        const fetchNewProfilesCreatedTrends = async () => {
            setNewProfilesCreatedTrendsLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/new-profiles-created-trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            try {
                const response = await axios.get(apiUrl, { params });
                captureDataThrough(response);
                const normalized = normalizeArrayPayload(response.data);
                setNewProfilesCreatedTrends(normalized);
            } catch (error: any) {
                console.error('❌ Error fetching new profiles created trends:', error);
                setNewProfilesCreatedTrends([]);
            } finally {
                setNewProfilesCreatedTrendsLoading(false);
            }
        };

        const fetchCustomerBaseProfileSplit = async () => {
            setCustomerBaseProfileSplitLoading(true);
            const apiUrl = `${getAdminApiBaseUrl()}/customers/customer-base-profile-split`;
            const params = {
                from_date: fromDate,
                to_date: toDate
            };

            try {
                const response = await axios.get(apiUrl, { params });
                captureDataThrough(response);
                setCustomerBaseProfileSplit(response.data || null);
            } catch (error: any) {
                console.error('❌ Error fetching customer base profile split:', error);
                setCustomerBaseProfileSplit(null);
            } finally {
                setCustomerBaseProfileSplitLoading(false);
            }
        };

        fetchTrendData();
        fetchConversionData();
        fetchVisitorData();
        fetchVisitorLoginSplit();
        fetchVisitorOnboardingSplit();
        fetchNewCustomerOnboardingSplit();
        fetchNewProfilesCreatedTrends();
        fetchCustomerBaseProfileSplit();
        fetchCustomerInsights();
        fetchVisitorBreakdowns();
    }, [period, fromDate, toDate]);

    const handlePeriodChange = (event: any) => {
        setPeriod(event.target.value);
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);

        if (!range) {
            return; // Don't update dates if "Custom" is selected
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 }); // Monday start
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
    };

    const exportDateParams = { from_date: fromDate, to_date: toDate };

    return (
        <Box sx={{ padding: 2 }}>
            <Card>
                <DashboardPanelHeader
                    title="Customer Onboarding Trends"
                    subheader="Shows how many people became customers by registering with their mobile number during the selected date range."
                    exportFilename="customer-onboarding-trends"
                    exportPanel="customer_trends"
                    exportParams={exportDateParams}
                />
                <CardContent>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select
                                    value={period}
                                    onChange={handlePeriodChange}
                                    label="Period"
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} onChange={handleQuickRangeChange} label="Quick Range">
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}
                    {loading ? (
                        <Skeleton height={300} />
                    ) : trendData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <p>No customer data available for the selected date range</p>
                            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', textAlign: 'center' }}>
                                <p><strong>Debug Info:</strong></p>
                                <p>Period: {period}</p>
                                <p>From Date: {fromDate}</p>
                                <p>To Date: {toDate}</p>
                                <p>API URL: {getAdminApiBaseUrl()}/customers/trends</p>
                                <p style={{ marginTop: '10px', fontSize: '0.75rem' }}>
                                    Check browser console (F12) and server logs for detailed debugging information
                                </p>
                            </Box>
                        </Box>
                    ) : (
                        <CustomerTrendChart data={trendData} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="New Profiles Created"
                    subheader="Count of customer profiles by first save date (profile_first_saved_at), not last profile edit. Uses the same date range and period filters as Customer Onboarding Trends above."
                    exportFilename="new-profiles-created"
                    exportPanel="new_profiles_created_trends"
                    exportParams={{ ...exportDateParams, period }}
                />
                <CardContent>
                    {newProfilesCreatedTrendsLoading ? (
                        <Skeleton height={300} />
                    ) : newProfilesCreatedTrends.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No new profile data available for the selected date range</p>
                        </Box>
                    ) : (
                        <CustomerTrendChart
                            data={newProfilesCreatedTrends}
                            period={period}
                            seriesName="New Profiles"
                            valueLabel="profiles"
                        />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="Customer Profile Completion"
                    subheader="Shows registered customers who still have a temporary user ID versus customers who have updated their name, during the selected date range."
                    exportFilename="customer-profile-completion"
                    exportPanel="customer_conversion"
                    exportParams={exportDateParams}
                />
                <CardContent>
                    {conversionLoading ? (
                        <Skeleton height={360} />
                    ) : conversionData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No profile completion data available for the selected date range</p>
                        </Box>
                    ) : (
                        <CustomerConversionChart data={conversionData} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="New Customer Profile Health"
                    subheader="Shows customers who registered in each period, split by profile completeness: phone only (temporary user ID), name updated, or full company profile (name, company, state, PIN)."
                    exportFilename="new-customer-profile-health"
                    exportPanel="new_customer_onboarding_split"
                    exportParams={{ ...exportDateParams, period }}
                />
                <CardContent>
                    {newCustomerOnboardingSplitLoading ? (
                        <Skeleton height={380} />
                    ) : newCustomerOnboardingSplit.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No new customer profile health data available for the selected date range</p>
                        </Box>
                    ) : (
                        <NewCustomerOnboardingChart data={newCustomerOnboardingSplit} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="Customer Profile Mix"
                    subheader="Share of customers who registered in the selected date range, split by no phone number, phone only, or complete company details (name, company, state, PIN)."
                    exportFilename="customer-base-profile-mix"
                    exportPanel="customer_base_profile_split"
                    exportParams={exportDateParams}
                />
                <CardContent>
                    {customerBaseProfileSplitLoading ? (
                        <Skeleton height={360} />
                    ) : !customerBaseProfileSplit?.segments?.length ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No customer profile mix data available for the selected date range</p>
                        </Box>
                    ) : (
                        <CustomerBaseProfilePieChart data={customerBaseProfileSplit} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="New vs Returning Visitor Conversion"
                    subheader="Shows which visitors are more likely to become customers. A new visitor is in their first visit period. A returning visitor has visited before. Registration rate means the percentage of those visitors who registered in that same period."
                    exportFilename="new-vs-returning-visitor-conversion"
                    exportPanel="new_returning_conversion"
                    exportParams={{ ...exportDateParams, period }}
                />
                <CardContent>
                    {customerInsightsLoading ? (
                        <Skeleton height={360} />
                    ) : !customerInsights?.new_returning_conversion?.length ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No new vs returning conversion data available for the selected date range</p>
                        </Box>
                    ) : (
                        <NewReturningVisitorConversionChart
                            data={customerInsights.new_returning_conversion}
                            period={period}
                        />
                    )}
                </CardContent>
            </Card>

            <Grid container spacing={2} sx={{ marginTop: 1 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <DashboardPanelHeader
                            title="Time to Register / Complete Profile"
                            subheader="Groups customers by how quickly they acted. Registered customers are grouped by time from first website visit to registration. Completed profiles are grouped by time from registration to replacing the temporary user ID with real profile details."
                            exportFilename="time-to-register-profile"
                            exportPanel="time_to_register_profile"
                            exportParams={exportDateParams}
                        />
                        <CardContent>
                            {customerInsightsLoading ? (
                                <Skeleton height={340} />
                            ) : (
                                <TimeToRegisterProfileChart data={customerInsights?.time_to_register_profile || []} />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <DashboardPanelHeader
                            title="Active vs Dormant Customers"
                            subheader="Groups registered customers by their most recent website activity. This helps sales decide who is warm, who needs follow-up, and who may need reactivation."
                            exportFilename="active-vs-dormant-customers"
                            exportPanel="active_dormant"
                        />
                        <CardContent>
                            {customerInsightsLoading ? (
                                <Skeleton height={340} />
                            ) : (
                                <ActiveDormantCustomersChart
                                    data={customerInsights?.active_dormant_customers || []}
                                    onBarClick={handleActiveDormantBarClick}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="Logged-in vs Anonymous Visitor Trend"
                    subheader="Shows how much website traffic came from known people versus unknown visitors. Logged-in visitors are people who logged in or registered during that period. Anonymous visitors browsed without logging in."
                    exportFilename="logged-in-vs-anonymous-visitors"
                    exportPanel="visitor_login_split"
                    exportParams={exportDateParams}
                />
                <CardContent>
                    {visitorLoginSplitLoading ? (
                        <Skeleton height={360} />
                    ) : visitorLoginSplit.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No visitor login data available for the selected date range</p>
                        </Box>
                    ) : (
                        <VisitorLoginSplitChart data={visitorLoginSplit} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="Visitor Trends"
                    subheader="Shows unique website visitors during the selected date range, split between first-time visitors and people returning to the website."
                    exportFilename="visitor-trends"
                    exportPanel="visitor_trends"
                    exportParams={exportDateParams}
                />
                <CardContent>
                    {visitorLoading ? (
                        <Skeleton height={300} />
                    ) : visitorData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No visitor data available for the selected date range</p>
                        </Box>
                    ) : (
                        <VisitorTrendChart data={visitorData} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <DashboardPanelHeader
                    title="Visitor Onboarding Stages"
                    subheader="Shows unique website visitors in each period, split into three buckets: anonymous browsers, registered users with phone only, and users who have completed company profile details (name, company, state, PIN)."
                    exportFilename="visitor-onboarding-stages"
                    exportPanel="visitor_onboarding_split"
                    exportParams={{ ...exportDateParams, period }}
                />
                <CardContent>
                    {visitorOnboardingSplitLoading ? (
                        <Skeleton height={380} />
                    ) : visitorOnboardingSplit.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No visitor onboarding data available for the selected date range</p>
                        </Box>
                    ) : (
                        <VisitorOnboardingSplitChart data={visitorOnboardingSplit} period={period} />
                    )}
                </CardContent>
            </Card>

            <VisitorBreakdownCharts
                data={visitorBreakdowns}
                loading={visitorBreakdownLoading}
                exportParams={exportDateParams}
            />

            <ActiveDormantCustomerModal
                open={activeDormantModalOpen}
                bucket={selectedActiveDormantBucket}
                bucketLabel={selectedActiveDormantLabel}
                expectedCount={selectedActiveDormantCount}
                onClose={handleActiveDormantModalClose}
            />
        </Box>
    );
};

export default DashboardCustomers;
